import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { crmAPI } from '../../services/api';
import { useLanguage } from '../../context/LanguageContext';
import CRMLayout from './CRMLayout';

const TABS = [
  { key: '',                label: 'Барчаси' },
  { key: 'pending_review',  label: 'Кутилмоқда' },
  { key: 'published',       label: 'Чоп этилган' },
  { key: 'rejected',        label: 'Рад этилган' },
  { key: 'views',           label: 'Кўришлар' },
];

const statusStyle = (s) => {
  if (s === 'pending_review') return { bg: '#fefcbf', color: '#b7791f', label: 'Кутилмоқда' };
  if (s === 'published')      return { bg: '#c6f6d5', color: '#276749', label: 'Чоп этилган' };
  if (s === 'rejected')       return { bg: '#fed7d7', color: '#c53030', label: 'Рад этилган' };
  return { bg: '#f1f5f9', color: '#718096', label: s };
};

const CRMArticles = () => {
  const { t } = useLanguage();
  const [activeTab, setActiveTab] = useState('pending_review');
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [previewId, setPreviewId] = useState(null);
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [rejectingId, setRejectingId] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [actingId, setActingId] = useState(null);
  const [rejectingEditId, setRejectingEditId] = useState(null);
  const [rejectEditReason, setRejectEditReason] = useState('');

  const load = useCallback(async () => {
    if (activeTab === 'views') return;
    setLoading(true);
    try {
      const res = await crmAPI.getArticles(activeTab || undefined);
      setArticles(res.data);
    } catch {
      setArticles([]);
    } finally {
      setLoading(false);
    }
  }, [activeTab]);

  useEffect(() => { load(); }, [load]);

  const handlePreview = async (id) => {
    if (previewId === id) {
      setPreviewId(null);
      setPreviewHtml('');
      return;
    }
    setPreviewId(id);
    setPreviewHtml('');
    setPreviewLoading(true);
    try {
      const res = await crmAPI.getArticlePreview(id);
      setPreviewHtml(res.data);
    } catch {
      setPreviewHtml('<p style="padding:1rem">Кўриб чиқишда хатолик юз берди</p>');
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleApprove = async (id) => {
    setActingId(id);
    try {
      await crmAPI.approveArticle(id);
      load();
    } catch (err) {
      alert(err.response?.data?.error || 'Хатолик');
    } finally {
      setActingId(null);
    }
  };

  const handleRejectSubmit = async (id) => {
    if (!rejectReason.trim()) return;
    setActingId(id);
    try {
      await crmAPI.rejectArticle(id, rejectReason.trim());
      setRejectingId(null);
      setRejectReason('');
      load();
    } catch (err) {
      alert(err.response?.data?.error || 'Хатолик');
    } finally {
      setActingId(null);
    }
  };

  const handleApproveEdit = async (id) => {
    setActingId(id);
    try {
      await crmAPI.approveEdit(id);
      load();
    } catch (err) {
      alert(err.response?.data?.error || 'Хатолик');
    } finally {
      setActingId(null);
    }
  };

  const handleRejectEditSubmit = async (id) => {
    if (!rejectEditReason.trim()) return;
    setActingId(id);
    try {
      await crmAPI.rejectEdit(id, rejectEditReason.trim());
      setRejectingEditId(null);
      setRejectEditReason('');
      load();
    } catch (err) {
      alert(err.response?.data?.error || 'Хатолик');
    } finally {
      setActingId(null);
    }
  };

  return (
    <CRMLayout>
      <div style={{ padding: '2rem' }}>
        <h1 style={{ fontSize: '1.5rem', fontWeight: '800', color: '#1a365d', margin: '0 0 1.5rem' }}>
          Мақолалар
        </h1>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.5rem', borderBottom: '2px solid #e2e8f0' }}>
          {TABS.map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              style={{
                padding: '0.5rem 1.1rem', border: 'none', cursor: 'pointer', fontSize: '0.88rem',
                fontWeight: activeTab === tab.key ? '700' : '400',
                color: activeTab === tab.key ? '#1a365d' : '#718096',
                backgroundColor: 'transparent',
                borderBottom: activeTab === tab.key ? '2px solid #1a365d' : '2px solid transparent',
                marginBottom: '-2px', fontFamily: 'inherit',
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {activeTab === 'views' ? (
          <ArticleViews />
        ) : loading ? (
          <div style={{ color: '#718096', textAlign: 'center', padding: '3rem' }}>Юкланмоқда...</div>
        ) : articles.length === 0 ? (
          <div style={{ color: '#718096', textAlign: 'center', padding: '3rem' }}>Мақолалар йўқ</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {articles.map(a => {
              const st = statusStyle(a.status);
              const dateStr = a.submitted_at || a.published_at || a.created_at;
              return (
                <div key={a.id} style={{
                  backgroundColor: 'white', borderRadius: '12px', border: '1px solid #e2e8f0',
                  padding: '1.25rem', boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '1rem', flexWrap: 'wrap' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '0.4rem', flexWrap: 'wrap' }}>
                        <span style={{ backgroundColor: st.bg, color: st.color, fontSize: '0.75rem', fontWeight: '600', padding: '0.2rem 0.65rem', borderRadius: '20px' }}>
                          {st.label}
                        </span>
                        {a.has_pending_edit && (
                          <span style={{ backgroundColor: '#fefcbf', color: '#b7791f', fontSize: '0.75rem', fontWeight: '600', padding: '0.2rem 0.65rem', borderRadius: '20px' }}>
                            Таҳрир бор
                          </span>
                        )}
                        {a.category && (
                          <span style={{ fontSize: '0.78rem', color: '#718096' }}>
                            {t(`practiceAreas.${a.category}`)}
                          </span>
                        )}
                        <span style={{ fontSize: '0.78rem', color: '#a0aec0' }}>
                          #{a.id} · {dateStr ? new Date(dateStr).toLocaleDateString('uz-UZ') : ''}
                        </span>
                      </div>

                      <div style={{ fontWeight: '600', color: '#2d3748', marginBottom: '0.2rem' }}>
                        {a.title}
                      </div>
                      <div style={{ color: '#718096', fontSize: '0.85rem' }}>
                        Муаллиф: {a.author_name}
                      </div>

                      {a.status === 'rejected' && a.rejection_reason && (
                        <div style={{ marginTop: '0.6rem', backgroundColor: '#fed7d7', border: '1px solid #feb2b2', borderRadius: '8px', padding: '0.6rem 0.75rem', fontSize: '0.85rem', color: '#c53030' }}>
                          Сабаб: {a.rejection_reason}
                        </div>
                      )}
                      {a.edit_rejection_reason && !a.has_pending_edit && (
                        <div style={{ marginTop: '0.6rem', backgroundColor: '#fff3cd', border: '1px solid #ffc107', borderRadius: '8px', padding: '0.6rem 0.75rem', fontSize: '0.85rem', color: '#856404' }}>
                          Таҳрир рад сабаби: {a.edit_rejection_reason}
                        </div>
                      )}
                    </div>

                    {/* Actions */}
                    <div style={{ display: 'flex', gap: '0.5rem', flexShrink: 0, flexWrap: 'wrap' }}>
                      <button onClick={() => handlePreview(a.id)} style={btn('#718096', 'small')}>
                        {previewId === a.id ? 'Ёпиш' : 'Кўриш'}
                      </button>
                      {a.status === 'published' && (
                        <a
                          href={`/legal-topics/${a.slug}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ ...btn('#3182ce', 'small'), textDecoration: 'none', display: 'inline-flex', alignItems: 'center' }}
                        >
                          Сайтда
                        </a>
                      )}
                      {a.status === 'pending_review' && (
                        <>
                          <button
                            onClick={() => handleApprove(a.id)}
                            disabled={actingId === a.id}
                            style={btn(actingId === a.id ? '#a0aec0' : '#38a169', 'small')}
                          >
                            Тасдиқлаш
                          </button>
                          <button
                            onClick={() => setRejectingId(rejectingId === a.id ? null : a.id)}
                            disabled={actingId === a.id}
                            style={btn('#e53e3e', 'small')}
                          >
                            Рад этиш
                          </button>
                        </>
                      )}
                      {a.has_pending_edit && (
                        <>
                          <button
                            onClick={() => handleApproveEdit(a.id)}
                            disabled={actingId === a.id}
                            style={btn(actingId === a.id ? '#a0aec0' : '#38a169', 'small')}
                          >
                            Таҳрирни тасдиқ
                          </button>
                          <button
                            onClick={() => setRejectingEditId(rejectingEditId === a.id ? null : a.id)}
                            disabled={actingId === a.id}
                            style={btn('#e53e3e', 'small')}
                          >
                            Таҳрирни рад
                          </button>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Reject reason box */}
                  {rejectingId === a.id && (
                    <div style={{ marginTop: '1rem', borderTop: '1px solid #e2e8f0', paddingTop: '1rem' }}>
                      <textarea
                        rows={3}
                        value={rejectReason}
                        onChange={e => setRejectReason(e.target.value)}
                        placeholder="Рад этиш сабабини ёзинг..."
                        style={{ width: '100%', padding: '0.75rem', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '0.9rem', resize: 'vertical', boxSizing: 'border-box', fontFamily: 'inherit' }}
                      />
                      <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem', justifyContent: 'flex-end' }}>
                        <button onClick={() => { setRejectingId(null); setRejectReason(''); }} style={btn('#718096', 'small')}>Бекор</button>
                        <button
                          onClick={() => handleRejectSubmit(a.id)}
                          disabled={actingId === a.id || !rejectReason.trim()}
                          style={btn(actingId === a.id ? '#a0aec0' : '#e53e3e', 'small')}
                        >
                          {actingId === a.id ? 'Юборилмоқда...' : 'Рад этиш'}
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Reject-edit reason box */}
                  {rejectingEditId === a.id && (
                    <div style={{ marginTop: '1rem', borderTop: '1px solid #e2e8f0', paddingTop: '1rem' }}>
                      <textarea
                        rows={3}
                        value={rejectEditReason}
                        onChange={e => setRejectEditReason(e.target.value)}
                        placeholder="Таҳрирни рад этиш сабабини ёзинг..."
                        style={{ width: '100%', padding: '0.75rem', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '0.9rem', resize: 'vertical', boxSizing: 'border-box', fontFamily: 'inherit' }}
                      />
                      <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem', justifyContent: 'flex-end' }}>
                        <button onClick={() => { setRejectingEditId(null); setRejectEditReason(''); }} style={btn('#718096', 'small')}>Бекор</button>
                        <button
                          onClick={() => handleRejectEditSubmit(a.id)}
                          disabled={actingId === a.id || !rejectEditReason.trim()}
                          style={btn(actingId === a.id ? '#a0aec0' : '#e53e3e', 'small')}
                        >
                          {actingId === a.id ? 'Юборилмоқда...' : 'Рад этиш'}
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Preview */}
                  {previewId === a.id && (
                    <div style={{ marginTop: '1rem', borderTop: '1px solid #e2e8f0', paddingTop: '1rem' }}>
                      {previewLoading ? (
                        <div style={{ color: '#718096', textAlign: 'center', padding: '2rem' }}>Юкланмоқда...</div>
                      ) : (
                        <iframe
                          title={`preview-${a.id}`}
                          srcDoc={previewHtml}
                          style={{ width: '100%', height: '70vh', border: '1px solid #e2e8f0', borderRadius: '8px' }}
                        />
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </CRMLayout>
  );
};

const btn = (bg, size) => ({
  backgroundColor: bg, color: 'white', border: 'none',
  padding: size === 'small' ? '0.4rem 0.9rem' : '0.5rem 1.1rem',
  borderRadius: '8px', fontSize: '0.85rem', fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit',
});

// ─── Article views sub-component ──────────────────────────────────────────────
const PAGE_SIZE = 20;
const tdStyle = { padding: '0.75rem 1rem', fontSize: '0.88rem', color: '#2d3748' };
const filterLabel = { display: 'block', fontSize: '0.78rem', fontWeight: '600', color: '#4a5568', marginBottom: '0.3rem' };
const TRIGGER_STYLE = {
  display: 'flex', alignItems: 'center', gap: '0.5rem',
  backgroundColor: 'white', border: '1px solid #e2e8f0', borderRadius: '8px',
  padding: '0.5rem 0.9rem', cursor: 'pointer', fontFamily: 'inherit',
  fontSize: '0.88rem', color: '#2d3748', fontWeight: '500', minWidth: '140px',
  justifyContent: 'space-between', userSelect: 'none', boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
};

const ViewsTable = ({ cols, children }) => (
  <div style={{ backgroundColor: 'white', borderRadius: '12px', border: '1px solid #e2e8f0', overflow: 'auto' }}>
    <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '600px' }}>
      <thead>
        <tr style={{ backgroundColor: '#f7fafc' }}>
          {cols.map(c => <th key={c} style={{ padding: '0.75rem 1rem', textAlign: 'left', fontSize: '0.78rem', fontWeight: '600', color: '#718096', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>{c}</th>)}
        </tr>
      </thead>
      <tbody>{children}</tbody>
    </table>
  </div>
);

const ViewsPagination = ({ page, totalPages, onPage, total }) => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '0.75rem', flexWrap: 'wrap', gap: '0.5rem' }}>
    <div style={{ fontSize: '0.82rem', color: '#718096' }}>Жами: {total} та</div>
    {totalPages > 1 && (
      <div style={{ display: 'flex', gap: '0.3rem' }}>
        {[
          { label: '‹', onClick: () => onPage(p => Math.max(1, p - 1)), disabled: page === 1 },
          ...Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
            const n = totalPages <= 7 ? i + 1 : page <= 4 ? i + 1 : page >= totalPages - 3 ? totalPages - 6 + i : page - 3 + i;
            return { label: n, onClick: () => onPage(n), active: page === n };
          }),
          { label: '›', onClick: () => onPage(p => Math.min(totalPages, p + 1)), disabled: page === totalPages },
        ].map((b, i) => (
          <button key={i} onClick={b.onClick} disabled={b.disabled} style={{
            minWidth: '32px', height: '32px', padding: '0 0.4rem', border: '1px solid #e2e8f0',
            borderRadius: '6px', cursor: b.disabled ? 'not-allowed' : 'pointer', fontSize: '0.85rem',
            backgroundColor: b.active ? '#1a365d' : 'white',
            color: b.active ? 'white' : b.disabled ? '#cbd5e0' : '#4a5568',
            fontWeight: b.active ? '700' : '400',
          }}>{b.label}</button>
        ))}
      </div>
    )}
  </div>
);

const AuthorFilter = ({ value, onChange, options }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const selected = options.find(o => o.value === value)?.label || 'Барчаси';
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);
  return (
    <div ref={ref}>
      <div style={filterLabel}>Муаллиф</div>
      <div style={{ position: 'relative' }}>
        <div onClick={() => setOpen(p => !p)} style={{ ...TRIGGER_STYLE, color: value ? '#1a365d' : '#718096' }}>
          <span>{selected}</span>
          <svg width="12" height="8" viewBox="0 0 12 8" fill="none"><path d="M1 1l5 5 5-5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
        </div>
        {open && (
          <div style={{ position: 'absolute', top: '100%', left: 0, zIndex: 100, backgroundColor: 'white', border: '1px solid #e2e8f0', borderRadius: '8px', boxShadow: '0 4px 16px rgba(0,0,0,0.12)', minWidth: '180px', marginTop: '4px', overflow: 'hidden' }}>
            {[{ value: '', label: 'Барчаси' }, ...options].map(o => (
              <div key={o.value} onClick={() => { onChange(o.value); setOpen(false); }}
                style={{ padding: '0.55rem 1rem', fontSize: '0.88rem', cursor: 'pointer', backgroundColor: value === o.value ? '#ebf8ff' : 'transparent', color: value === o.value ? '#1a365d' : '#2d3748', fontWeight: value === o.value ? '600' : '400' }}>
                {o.label}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const DateFilter = ({ label, value, onChange }) => {
  const ref = useRef(null);
  const display = value ? new Date(value + 'T00:00:00').toLocaleDateString('uz-UZ') : null;
  return (
    <div>
      <div style={filterLabel}>{label}</div>
      <div style={{ position: 'relative', display: 'inline-block' }} onClick={() => { try { ref.current?.showPicker(); } catch {} }}>
        <div style={{ ...TRIGGER_STYLE, minWidth: '140px', paddingRight: '2rem', color: value ? '#1a365d' : '#718096', pointerEvents: 'none' }}>
          <span>{display || 'Сана'}</span>
        </div>
        <input ref={ref} type="date" value={value} onChange={onChange}
          style={{ position: 'absolute', inset: 0, opacity: 0, cursor: 'pointer', width: '100%' }} />
        {value && (
          <button onClick={(e) => { e.stopPropagation(); onChange({ target: { value: '' } }); }}
            style={{ position: 'absolute', right: '0.5rem', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#a0aec0', fontSize: '1rem', lineHeight: 1 }}>×</button>
        )}
      </div>
    </div>
  );
};

const ArticleViews = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [authorFilter, setAuthorFilter] = useState('');
  const [page, setPage] = useState(1);

  useEffect(() => {
    setLoading(true);
    crmAPI.getArticleViewStats({ dateFrom, dateTo })
      .then(res => setRows(res.data))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [dateFrom, dateTo]);

  const authors = useMemo(() => [...new Set(rows.map(r => r.authorName).filter(Boolean))].sort(), [rows]);
  const filtered = useMemo(() => authorFilter ? rows.filter(r => r.authorName === authorFilter) : rows, [rows, authorFilter]);
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paged = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const totalViews = filtered.reduce((s, r) => s + r.totalViews, 0);
  const totalUnique = filtered.reduce((s, r) => s + r.uniqueViews, 0);
  const resetPage = () => setPage(1);

  if (loading) return <div style={{ color: '#718096', textAlign: 'center', padding: '3rem' }}>Юкланмоқда...</div>;

  return (
    <div>
      <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <AuthorFilter value={authorFilter} onChange={(v) => { setAuthorFilter(v); resetPage(); }} options={authors.map(a => ({ value: a, label: a }))} />
        <DateFilter label="Дан" value={dateFrom} onChange={e => { setDateFrom(e.target.value); resetPage(); }} />
        <DateFilter label="Гача" value={dateTo} onChange={e => { setDateTo(e.target.value); resetPage(); }} />
      </div>

      <ViewsTable cols={['Сарлавҳа', 'Муаллиф', 'Эълон қилинган сана', 'Жами кўришлар', 'Ноёб кўришлар']}>
        {paged.length === 0
          ? <tr><td colSpan={5} style={{ textAlign: 'center', padding: '2rem', color: '#718096' }}>Маълумот йўқ</td></tr>
          : paged.map(r => (
            <tr key={r.slug} style={{ borderTop: '1px solid #f0f4f8' }}>
              <td style={tdStyle}>
                <a href={`/legal-topics/${r.slug}`} target="_blank" rel="noopener noreferrer" style={{ color: '#3182ce', textDecoration: 'none' }}>{r.title}</a>
              </td>
              <td style={tdStyle}>{r.authorName || '—'}</td>
              <td style={tdStyle}>{r.publishedAt ? new Date(r.publishedAt).toLocaleDateString('uz-UZ') : '—'}</td>
              <td style={{ ...tdStyle, fontWeight: '600' }}>{r.totalViews}</td>
              <td style={tdStyle}>{r.uniqueViews}</td>
            </tr>
          ))}
        {filtered.length > 0 && (
          <tr style={{ backgroundColor: '#f7fafc', borderTop: '2px solid #e2e8f0' }}>
            <td style={{ ...tdStyle, fontWeight: '700' }} colSpan={3}>Жами ({filtered.length} та пост)</td>
            <td style={{ ...tdStyle, fontWeight: '800', color: '#1a365d' }}>{totalViews}</td>
            <td style={{ ...tdStyle, fontWeight: '800', color: '#1a365d' }}>{totalUnique}</td>
          </tr>
        )}
      </ViewsTable>

      <ViewsPagination page={page} totalPages={totalPages} onPage={setPage} total={filtered.length} />
    </div>
  );
};

export default CRMArticles;
